import { useEffect, useRef } from 'react';
import type {
  AnomalyAssessment,
  AnomalyWorkstate,
  AnomalyWorkstateSource,
  MicroStatus,
} from './api';

/**
 * Cross-window workstate bridge (#264 — seat-claim propagation, same machine).
 *
 * When one window persists a queue-row working-state change (claim-ack, plain
 * ack, micro-status, owner), every sibling same-origin window (COP rail +
 * /panel — the same desk) should reflect it instantly rather than waiting for
 * its next queue poll. Same BroadcastChannel idiom as pinnedFeeds/panelBridge.
 *
 * No localStorage here: the SERVER row is the durable truth (a late-joining
 * window simply fetches), so the bridge only carries live deltas. Cross-DESK
 * propagation (other machines) rides the queue/wall polls — see
 * AnomalyQueuePanel QUEUE_POLL_MS and the wall's useWallQueueItems sweep.
 */

export const WORKSTATE_CHANNEL = 'tsr-workstate';

/** This window's bridge identity. The originating window already applied the
 *  server row directly (optimistic patch + response reconcile), so its own
 *  listener must NOT re-apply the echo — BroadcastChannel delivers a post to
 *  every OTHER channel instance, including ones in the same window. */
const WINDOW_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `w-${Math.random().toString(36).slice(2)}`;

export interface WorkstateMessage {
  type: 'workstate:update';
  /** Originating window (see WINDOW_ID) — receivers skip their own messages. */
  from: string;
  sourceType: AnomalyWorkstateSource;
  sourceId: string;
  work_acknowledged_at: string | null;
  micro_status: MicroStatus;
  /** #331 reversible assessment axis. Optional on the wire so an older sibling
   *  window's message (pre-0146) still validates — consumers ignore an absent value. */
  assessment?: AnomalyAssessment;
  owner_label: string | null;
}

const SOURCE_TYPES: readonly string[] = [
  'hotspot',
  'cap',
  'smoke',
  'ice_storm',
  'flood',
  'media_signal',
];

export function isWorkstateMessage(data: unknown): data is WorkstateMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return (
    m.type === 'workstate:update' &&
    typeof m.from === 'string' &&
    typeof m.sourceType === 'string' &&
    SOURCE_TYPES.includes(m.sourceType) &&
    typeof m.sourceId === 'string' &&
    (m.work_acknowledged_at === null || typeof m.work_acknowledged_at === 'string') &&
    (m.micro_status === 'new' || m.micro_status === 'working' || m.micro_status === 'resolving') &&
    (m.assessment === undefined ||
      m.assessment === 'unconfirmed' ||
      m.assessment === 'confirmed' ||
      m.assessment === 'false_positive') &&
    (m.owner_label === null || typeof m.owner_label === 'string')
  );
}

/** Push a persisted workstate row to sibling windows (call after a 2xx PUT). */
export function broadcastWorkstate(ws: AnomalyWorkstate): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(WORKSTATE_CHANNEL);
  channel.postMessage({
    type: 'workstate:update',
    from: WINDOW_ID,
    sourceType: ws.source_type,
    sourceId: ws.source_id,
    work_acknowledged_at: ws.work_acknowledged_at,
    micro_status: ws.micro_status,
    assessment: ws.assessment,
    owner_label: ws.owner_label,
  } satisfies WorkstateMessage);
  channel.close();
}

/**
 * Subscribe to sibling-window workstate updates. Subscribes once; always calls
 * the LATEST handler (ref-routed), so callers can pass an inline closure.
 * Inert without BroadcastChannel.
 */
export function useWorkstateBridge(onUpdate: (msg: WorkstateMessage) => void): void {
  const handlerRef = useRef(onUpdate);
  handlerRef.current = onUpdate;
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(WORKSTATE_CHANNEL);
    channel.onmessage = (e: MessageEvent) => {
      // Skip this window's own echoes — it already applied the server row.
      if (isWorkstateMessage(e.data) && e.data.from !== WINDOW_ID) handlerRef.current(e.data);
    };
    return () => channel.close();
  }, []);
}
